import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChangeReason, CommitReason, NumericStepperProps, ValueCommitEvent } from '.';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { toLatinNumberingSystemLocale } from '../../locale/localeUtils';
import { isNullish } from '@sas/nova-core/typeguards';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import {
  checkValidNumberFormat,
  getNumberSeparators,
  InputProps,
  NumberFormatOptions,
  parseAndFormatInput,
  validateNumericInput,
  validateRangeInput,
} from '../Input';
import {
  NumericStepperInput,
  SpinButtons,
  SpinButtonsValueChangeEvent,
  SpinButtonsValueCommitEvent,
  StateChangeEvent,
} from './__internal__';

import './NumericStepper.css';

type ValueState = { status: InputProps['status']; message: InputProps['message'] };

const DEFAULT_MINIMUM_FRACTION_DIGITS = 0;
const DEFAULT_MAXIMUM_FRACTION_DIGITS = 3;

/**
 * The minimum value allowed.
 */
export const MINIMUM_VALUE = -999999999;
/**
 * The maximum value allowed.
 */
export const MAXIMUM_VALUE = 999999999;
/**
 * Minimum number of decimal points.
 */
export const MINIMUM_FRACTION_DIGITS = 0;
// if the value is 999_999_999, there are only 7 decimal places can be valid.
/**
 * Maximum number of decimal points.
 */
export const MAXIMUM_FRACTION_DIGITS = 7;

export const NumericStepper = forwardRef<HTMLInputElement, NumericStepperProps>(
  (
    {
      allowIntegerOnly = false,
      enableRevertToCommitted = false,
      disabled = false,
      editMode = 'select',
      readOnly = false,
      required = false,
      maximum = MAXIMUM_VALUE,
      minimum = MINIMUM_VALUE,
      stepSize = 1,
      pageSize = stepSize * 2,
      // it can't be set to minimum here when it is undefined,
      // because consumer can't get informed for the change.
      value /* = minimum */,
      status: customizedStatus,
      message: customizedMessage,
      onChange,
      onCommit,
      ...props
    },
    ref
  ) => {
    const numberFormatOptions = useMemo(() => {
      const options = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

      if (!allowIntegerOnly) {
        options.minimumFractionDigits =
          props.numberFormatOptions?.minimumFractionDigits ?? DEFAULT_MINIMUM_FRACTION_DIGITS;
        options.maximumFractionDigits =
          props.numberFormatOptions?.maximumFractionDigits ??
          Math.max(DEFAULT_MAXIMUM_FRACTION_DIGITS, options.minimumFractionDigits);
      }

      return options;
    }, [
      allowIntegerOnly,
      props.numberFormatOptions?.maximumFractionDigits,
      props.numberFormatOptions?.minimumFractionDigits,
    ]);

    assertNumberFormatOptionsValidity(numberFormatOptions);
    assertMinMaxValidity(minimum, maximum, numberFormatOptions.maximumFractionDigits);
    assertStepSizeValidity(stepSize, pageSize, numberFormatOptions.maximumFractionDigits);

    const inputRef = useRef<HTMLInputElement | null>(null);
    // think of this state should not rerender the ui, then useRef instead of useState.
    const isLiveRef = useRef(false);

    // references for commit value
    const lastCommitInfoRef = useRef<ValueCommitEvent>();
    // when enable revert to commit, and an invalid value detected, this value will be emitted.
    const lastSuccessCommitValueRef = useRef<number>(minimum);
    // the display value in last onChange event
    const lastChangedValueRef = useRef<string | number | undefined>();

    const locale = useFormatLocale();

    const [valueState, setValueState] = useState<ValueState>({ status: undefined, message: '' });

    const maxLength = useMemo(() => {
      const options = {
        minimumFractionDigits: numberFormatOptions.maximumFractionDigits,
        maximumFractionDigits: numberFormatOptions.maximumFractionDigits,
      };

      return Math.max(
        parseAndFormatInput(minimum.toString(), undefined, options).formatValue.length,
        parseAndFormatInput(maximum.toString(), undefined, options).formatValue.length
      );
    }, [minimum, maximum, numberFormatOptions.maximumFractionDigits]);

    const fireValueChanged = useCallback(
      (displayValue: string, roundedRawValue: number, reason: ChangeReason) => {
        if (lastChangedValueRef.current !== displayValue) {
          lastChangedValueRef.current = displayValue;
          onChange({
            displayValue,
            numericValue: roundedRawValue,
            reason,
          });
        }
      },
      [onChange]
    );

    const fireValueCommitted = useCallback(
      (value: number | null | undefined, reason: CommitReason) => {
        const errorType =
          value === null && lastChangedValueRef.current !== undefined
            ? getErrorType({
                invalidValue: lastChangedValueRef.current,
                min: minimum,
                max: maximum,
                allowIntegerOnly,
                locale,
              })
            : undefined;

        if (
          lastCommitInfoRef.current === undefined ||
          lastCommitInfoRef.current.value !== value ||
          lastCommitInfoRef.current.reason !== reason ||
          lastCommitInfoRef.current.error !== errorType
        ) {
          lastCommitInfoRef.current = { value, reason, error: errorType };
          if (!isNullish(value)) {
            lastSuccessCommitValueRef.current = value;
          }

          onCommit?.({ value, reason, error: errorType });
        }
      },
      [allowIntegerOnly, locale, maximum, minimum, onCommit]
    );

    const onSpinButtonsValueChange = (e: SpinButtonsValueChangeEvent) => {
      const { formatValue, roundedRawValue } = parseValue(e.newValue, locale, numberFormatOptions);
      fireValueChanged(formatValue, roundedRawValue, 'press');
    };

    const onSpinButtonsValueCommit = (e: SpinButtonsValueCommitEvent) => {
      if (e.reason === 'blur') {
        // This is requested in NOVACOMMONS-4457
        fireValueCommitted(lastCommitInfoRef.current?.value, e.reason);
      } else {
        fireValueCommitted(e.value, e.reason);
      }
    };

    const earconInputFull = useEarcon({ source: EarconName.InputFull });
    const earconError = useEarcon({ source: EarconName.Error });

    const onInputStateChange = (e: StateChangeEvent) => {
      const { rawValue, formatValue, roundedRawValue } = parseValue(
        e.value,
        locale,
        numberFormatOptions
      );
      switch (e.state) {
        case 'change':
          isLiveRef.current = true;

          const validDecimalValue = getValidDecimalValue(e.value, locale, numberFormatOptions);
          const hasMoreDecimal = validDecimalValue !== e.value;

          if (hasMoreDecimal) {
            earconInputFull.playFromStart();
            const rawValidDecimalValue = parseValue(validDecimalValue, locale, numberFormatOptions)
              .rawValue;
            fireValueChanged(validDecimalValue, rawValidDecimalValue, 'type');
          } else {
            fireValueChanged(e.value, rawValue, 'type');
          }

          break;
        case 'enter':
        case 'blur':
          isLiveRef.current = false;
          const value = e.value;
          const { status, message } = getFinalStatusAndMessage(
            value,
            isLiveRef.current,
            locale,
            required,
            allowIntegerOnly,
            minimum,
            maximum,
            customizedStatus,
            customizedMessage
          );
          if (
            e.state === 'blur' &&
            (value === '' || status !== undefined) &&
            enableRevertToCommitted
          ) {
            const lastSuccessCommitFormatValue = parseValue(
              lastSuccessCommitValueRef.current,
              locale,
              numberFormatOptions
            ).formatValue;
            // invoke valueChange internally
            fireValueChanged(
              lastSuccessCommitFormatValue,
              lastSuccessCommitValueRef.current,
              'revise'
            );
            fireValueCommitted(lastSuccessCommitValueRef.current, 'revise');
          } else {
            // onChange
            if (status === undefined && lastChangedValueRef.current !== undefined) {
              // invoke valueChange internally
              fireValueChanged(value === '' ? '' : formatValue, roundedRawValue, e.state);
            } else {
              setValueState({ status, message });
            }

            // onCommit
            fireValueCommitted(
              status === undefined ? (value !== '' ? roundedRawValue : undefined) : null,
              e.state
            );
          }

          break;
        case 'focus':
          isLiveRef.current = true;
          // show raw number to edit in edit mode of 'combo'
          if (
            editMode === 'combo' &&
            valueState.status === undefined &&
            !isNaN(roundedRawValue) &&
            e.value.toString() !== roundedRawValue.toString()
          ) {
            // invoke ValueChange internally
            fireValueChanged(
              getLocaleStringValue(roundedRawValue, locale, {
                useGrouping: false,
                ...numberFormatOptions,
              }),
              roundedRawValue,
              'focus'
            );
          }
          break;
      }
    };

    const { rawValue, formatValue, roundedRawValue } = parseValue(
      value,
      locale,
      numberFormatOptions
    );

    const formattedMinimum = parseValue(minimum, locale, numberFormatOptions).formatValue;

    useEffect(() => {
      const input = inputRef.current;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (input?.disabled || input?.readOnly) {
          return;
        }

        if (isPrintableKey(e)) {
          if (
            input &&
            input.value.length === input.maxLength &&
            (input.selectionEnd ?? 0) - (input.selectionStart ?? 0) === 0
          ) {
            earconInputFull.playFromStart();
            return;
          } else if (isNumberIrrelevantKey(e.key, locale)) {
            earconError.playFromStart();
          }
        }
      };

      input?.addEventListener('keydown', handleKeyDown);

      return () => {
        input?.removeEventListener('keydown', handleKeyDown);
      };
    });

    useEffect(() => {
      // validate and update value state
      const { status, message } = getFinalStatusAndMessage(
        value,
        isLiveRef.current,
        locale,
        required,
        allowIntegerOnly,
        minimum,
        maximum,
        customizedStatus,
        customizedMessage
      );

      const isUntouched = lastCommitInfoRef.current?.value === undefined;
      if (!(value === '' && editMode === 'combo' && required && isUntouched)) {
        setValueState({ status, message });
      }

      const isInitial = lastChangedValueRef.current === undefined;
      const isUnexpectedValue = lastChangedValueRef.current !== value;

      // There should be always a value shown in select edit mode.
      // So if a blank string, an undefined or a Non-number value is provided
      // without from onChange event or when initializing,
      // the minimum will be supplied.
      const isInvalidValueToSelectMode =
        (status || value === '' || value === undefined) &&
        (isInitial || isUnexpectedValue) &&
        editMode === 'select';

      // When initializing a Numeric Stepper in combo edit mode with a undefined value,
      // the minimum value should be shown as well.
      const isInvalidValueToComboMode = value === undefined && isInitial && editMode === 'combo';

      if (isInvalidValueToComboMode || isInvalidValueToSelectMode) {
        // invoke valueChange internally
        fireValueChanged(formattedMinimum, minimum, 'revise');
        fireValueCommitted(minimum, 'revise');
        return;
      }

      // If a value is changed after formating, to notice user
      if (!isLiveRef.current && !status && value !== '' && rawValue !== roundedRawValue) {
        lastChangedValueRef.current = undefined;
        // invoke valueChange internally to notice user
        fireValueChanged(formatValue, roundedRawValue, 'revise');

        lastCommitInfoRef.current = {
          value: roundedRawValue,
          reason: undefined,
        };
        fireValueCommitted(roundedRawValue, 'revise');
        return;
      }

      // value is provided without from onChange event
      if (!isInitial && isUnexpectedValue) {
        lastChangedValueRef.current = formatValue;
      }
    }, [
      allowIntegerOnly,
      customizedMessage,
      customizedStatus,
      editMode,
      fireValueChanged,
      fireValueCommitted,
      formatValue,
      formattedMinimum,
      locale,
      maximum,
      minimum,
      rawValue,
      required,
      roundedRawValue,
      value,
    ]);

    // update aria-* in NumericStepperInput
    useEffect(() => {
      const input = inputRef.current;
      if (!input) return;

      if (value === undefined) {
        input.setAttribute('aria-valuetext', 'empty');
        input.removeAttribute('aria-valuenow');
        return;
      }

      if (isNaN(roundedRawValue)) {
        input.setAttribute('aria-valuetext', input.value);
        input.removeAttribute('aria-valuenow');
      } else {
        input.setAttribute('aria-valuenow', input.value);
        input.removeAttribute('aria-valuetext');
      }

      // Add an assertion to ensure the minimum and maximum be in round.
      input.setAttribute('aria-valuemin', minimum.toString());
      input.setAttribute('aria-valuemax', maximum.toString());
    }, [value, roundedRawValue, minimum, maximum]);

    const setInputRef = (input: HTMLInputElement) => {
      inputRef.current = input;
      setRef(ref, input);
    };

    if (readOnly) {
      return (
        <NumericStepperInput
          {...props}
          ref={setInputRef}
          readOnly
          disabled={disabled}
          editMode={editMode}
          maxLength={maxLength}
          value={getLocaleStringValue(value, locale)}
          onStateChange={() => {}}
        />
      );
    } else {
      return (
        <SpinButtons
          {...props}
          disabled={disabled}
          minimum={minimum}
          maximum={maximum}
          value={isNaN(roundedRawValue) ? undefined : roundedRawValue}
          stepSize={stepSize}
          pageSize={pageSize}
          onValueChange={onSpinButtonsValueChange}
          onValueCommit={onSpinButtonsValueCommit}
        >
          <NumericStepperInput
            {...props}
            disabled={disabled}
            editMode={editMode}
            required={required}
            maxLength={maxLength}
            value={
              isLiveRef.current || valueState.status || value === ''
                ? (value ?? '').toString()
                : formatValue
            }
            testId={postfixTestId(props.testId, 'inputField')}
            status={valueState.status}
            message={valueState.message}
            ref={setInputRef}
            onStateChange={onInputStateChange}
          />
        </SpinButtons>
      );
    }
  }
);

function assertNumberFormatOptionsValidity(options: NumberFormatOptions): void {
  if (options.minimumFractionDigits! < MINIMUM_FRACTION_DIGITS) {
    throw new Error(
      `The minimum number of fraction digits cannot be less than ${MINIMUM_FRACTION_DIGITS}.`
    );
  }

  if (options.maximumFractionDigits! > MAXIMUM_FRACTION_DIGITS) {
    throw new Error(
      `The maximum number of fraction digits cannot be larger than ${MAXIMUM_FRACTION_DIGITS}.`
    );
  }

  if (options.maximumFractionDigits! < options.minimumFractionDigits!) {
    throw new Error(
      `The maximum number of fraction digits cannot be less than the minimum number of fraction digits.`
    );
  }
}

function assertMinMaxValidity(min: number, max: number, maxFractionDigits: number): void {
  const fractionDigits = Math.max(
    min.toString().split('.')[1]?.length ?? 0,
    max.toString().split('.')[1]?.length ?? 0
  );

  if (fractionDigits > maxFractionDigits) {
    throw new Error(
      `The number of fraction digits in minimum/maximum cannot be larger than maximum fraction digits.`
    );
  }

  if (min < MINIMUM_VALUE) {
    throw new Error(`The minimum cannot be less than ${MINIMUM_VALUE}.`);
  }

  if (max > MAXIMUM_VALUE) {
    throw new Error(`The maximum cannot be greater than ${MAXIMUM_VALUE}.`);
  }

  if (max < min) {
    throw new Error(`The maximum cannot be less than minimum.`);
  }
}

function assertStepSizeValidity(
  stepSize: number,
  pageSize: number,
  maxFractionDigits: number
): void {
  const fractionDigits = Math.max(
    stepSize.toString().split('.')[1]?.length ?? 0,
    pageSize.toString().split('.')[1]?.length ?? 0,
    0
  );

  if (fractionDigits > maxFractionDigits) {
    throw new Error(
      `The number of fraction digits in stepSize/pageSize cannot be larger than maximum fraction digits.`
    );
  }

  if (stepSize <= 0) {
    throw new Error(`The stepSize cannot be less than or equals 0.`);
  }

  if (pageSize <= 0) {
    throw new Error(`The pageSize cannot be less than or equals 0.`);
  }

  if (pageSize < stepSize) {
    throw new Error(`The pageSize cannot be less than stepSize.`);
  }
}

function validate(
  value: number | string | undefined,
  isLive: boolean,
  locale: string,
  isRequired: boolean,
  allowIntegerOnly: boolean,
  minimum: number,
  maximum: number
) {
  const validateProps = {
    value: (value ?? '').toLocaleString(locale),
    isRequired,
    allowIntegersOnly: allowIntegerOnly,
    isLive,
    appLocale: locale,
    minValue: minimum,
    maxValue: maximum,
    inclusiveMode: 3,
  };

  return isLive
    ? validateNumericInput(
        validateProps.value,
        validateProps.appLocale,
        validateProps.isRequired,
        validateProps.allowIntegersOnly,
        validateProps.isLive
      )
    : validateRangeInput(validateProps);
}

function getFinalStatusAndMessage(
  value: number | string | undefined,
  isLive: boolean,
  locale: string,
  isRequired: boolean,
  allowIntegerOnly: boolean,
  minimum: number,
  maximum: number,
  customizedStatus: InputProps['status'],
  customizedMessage: InputProps['message']
): ValueState {
  const localeStringValue = getLocaleStringValue(value, locale);

  const validateState = validate(
    localeStringValue,
    isLive,
    locale,
    isRequired,
    allowIntegerOnly,
    minimum,
    maximum
  );

  // consumer status has higher priority
  if (!customizedStatus) {
    return { status: validateState.status, message: validateState.message };
  } else if (
    customizedStatus === 'alert' ||
    (customizedStatus === 'warning' && validateState.status !== 'alert')
  ) {
    return { status: customizedStatus, message: customizedMessage };
  } else {
    return { status: validateState.status, message: validateState.message };
  }
}

function getLocaleStringValue(
  value: number | string | undefined,
  locale = 'en-US',
  options?: Intl.NumberFormatOptions
): string {
  return value === undefined
    ? ''
    : typeof value === 'string' // if value is string, it is assumed locale string already.
    ? value
    : value.toLocaleString(toLatinNumberingSystemLocale(locale), options);
}

function parseValue(
  value: number | string | undefined,
  locale = 'en-US',
  options?: Intl.NumberFormatOptions
): { formatValue: string; rawValue: number; roundedRawValue: number } {
  const { rawValue, formatValue } = parseAndFormatInput(
    getLocaleStringValue(value, locale, options),
    locale,
    options
  );
  const roundedRawValue = round(rawValue, options!.maximumFractionDigits!);
  return { rawValue, formatValue: formatValue.replace(/\u200e/, ''), roundedRawValue };
}

// TODO This code may be should be put in useInputValidationAdapter
function round(value: number, fractionDigits: number): number {
  // 0.1235 cannot be rounded to 0.124 by (0.1235).toFix(3) directly
  const times = Math.pow(10, fractionDigits);
  return Number((value * times).toFixed(0)) / times;
}

function getErrorType({
  invalidValue,
  allowIntegerOnly,
  min,
  max,
  locale,
}: {
  invalidValue: string | number;
  allowIntegerOnly: boolean;
  min: number;
  max: number;
  locale?: string;
}): ValueCommitEvent['error'] {
  if (
    typeof invalidValue === 'string' &&
    (isNaN(Number(invalidValue)) ||
      invalidValue.trim() === '' ||
      !checkValidNumberFormat(invalidValue, locale, false))
  ) {
    return 'notNumber';
  }

  const value = Number(invalidValue);

  if (value > max) {
    return 'greaterThanMax';
  } else if (value < min) {
    return 'lessThanMin';
  } else if (allowIntegerOnly && Math.floor(value) < value) {
    return 'notInteger';
  }

  return undefined;
}

function getValidDecimalValue(value: string, locale = 'en-US', options?: Intl.NumberFormatOptions) {
  const { rawValue } = parseAndFormatInput(value, locale, options);
  if (isNaN(rawValue)) {
    return value;
  }

  const [decimalSeparator] = getNumberSeparators(locale);
  const decimalSeparatorPosition = value.indexOf(decimalSeparator);

  return decimalSeparatorPosition > -1
    ? value.substring(0, decimalSeparatorPosition) +
        value.substring(
          decimalSeparatorPosition,
          decimalSeparatorPosition + (options?.maximumFractionDigits ?? MAXIMUM_FRACTION_DIGITS) + 1
        )
    : value;
}

function isPrintableKey(e: KeyboardEvent) {
  if (e.ctrlKey || e.altKey || e.metaKey) {
    return false;
  }

  if (e.keyCode === 229 || e.key.length === 1) {
    return true;
  }

  return false;
}

function isNumberIrrelevantKey(key: string, locale: string) {
  const [decimalSeparator, g] = getNumberSeparators(locale);
  const groupingSeparator = g === '\\s' ? ' ' : g;

  const numberRelatedKeys = `0123456789+-${decimalSeparator}${groupingSeparator}`;

  return numberRelatedKeys.indexOf(key) === -1;
}
